import type { ApplyUpdateRow } from './ruleSchemas';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { IconBolt, IconBulb, IconPlus, IconTrash } from '@tabler/icons-react';
import type { ReactElement } from 'react';
import {
  ApplyField,
  ruleApplyBehaviorSchema,
  ruleApplyNotificationSchema,
  ruleApplyWritePolicySchema,
} from '@billing/validation/rules';
import { RuleClaimValueSelect } from './RuleClaimValueSelect';
import {
  getRuleClaimFieldOptions,
  getRuleClaimOptionValue,
} from './ruleClaimFieldOptions';
import {
  applyFieldOptions,
  timeBasedApplyFields,
  TIME_BASED_INTERVALS,
} from './constants';
import type { RuleBuilder } from './useRuleBuilder';

type ApplyPreviewProps = Pick<
  RuleBuilder,
  | 'translate'
  | 'applyUpdates'
  | 'applyBehavior'
  | 'applyNotification'
  | 'applyIntervalMinutes'
>;

export const RuleApplyDataPreview = (
  builder: ApplyPreviewProps
): ReactElement => {
  const { translate } = builder;
  const suggested = builder.applyBehavior === 'suggest';
  if (!suggested && builder.applyNotification === 'silent')
    return (
      <Text size="sm" c="dimmed">
        {translate('pages:rules.applyData.silentPreview')}
      </Text>
    );
  return (
    <Alert
      color="teal"
      variant="light"
      icon={suggested ? <IconBulb size={18} /> : <IconBolt size={18} />}
      title={translate(
        suggested
          ? 'pages:rules.applyData.suggestionTitle'
          : 'pages:rules.applyData.addedTitle'
      )}
    >
      <Stack gap="xs">
        {builder.applyUpdates.map((update) => {
          const options = getRuleClaimFieldOptions(update.field, translate);
          const option = options?.find(
            (item) => getRuleClaimOptionValue(item) === update.value
          );
          const value = timeBasedApplyFields.has(update.field)
            ? translate('pages:rules.applyData.intervalValue', {
                minutes: builder.applyIntervalMinutes,
              })
            : option?.label ??
              (update.value.trim() ||
                translate('pages:rules.applyData.valuePlaceholder'));
          const policy =
            update.field === ApplyField.TariffCode
              ? 'addIfMissing'
              : update.writePolicy ?? 'replace';
          return (
            <Text key={update.id} size="sm">
              {translate(`pages:rules.editApplyFields.${update.field}`)}:{' '}
              {value} · {translate(`pages:rules.applyData.${policy}`)}
            </Text>
          );
        })}
        <Group gap="md">
          <Text size="sm" c="blue">
            {translate(
              suggested
                ? 'pages:rules.applyData.apply'
                : 'pages:rules.applyData.undo'
            )}
          </Text>
          {suggested && (
            <Text size="sm" c="dimmed">
              {translate('pages:rules.applyData.dismiss')}
            </Text>
          )}
        </Group>
      </Stack>
    </Alert>
  );
};

const RuleApplyUpdateFields = ({
  builder,
  update,
  index,
}: {
  builder: RuleBuilder;
  update: ApplyUpdateRow;
  index: number;
}): ReactElement => {
  const { translate } = builder;
  const options = getRuleClaimFieldOptions(update.field, translate);
  const isTimeBased = timeBasedApplyFields.has(update.field);
  return (
    <Paper key={update.id} withBorder radius="md" p="sm">
      <Group align="flex-end" wrap="wrap">
        <Select
          label={translate('pages:rules.editControls.updateField')}
          data={applyFieldOptions.map((option) => ({
            value: option.value,
            label: translate(`pages:rules.editApplyFields.${option.value}`),
          }))}
          value={update.field}
          onChange={(value) => {
            const field = applyFieldOptions.find(
              (option) => option.value === value
            );
            if (field)
              builder.updateApplyUpdate(update.id, {
                field: field.value,
                value: '',
              });
          }}
          allowDeselect={false}
          searchable
          style={{ flex: '1 1 180px' }}
          data-testid={
            index === 0 ? 'rule-apply-field' : `rule-apply-field-${index}`
          }
        />
        {isTimeBased && (
          <Select
            label={translate('pages:rules.editControls.applyValue')}
            data={builder.localizedTimeBasedIntervalOptions}
            value={builder.applyIntervalMinutes}
            onChange={(value) =>
              builder.setApplyIntervalMinutes(value || TIME_BASED_INTERVALS[0])
            }
            allowDeselect={false}
            data-testid="rule-apply-interval-minutes"
          />
        )}
        {!isTimeBased && options && (
          <RuleClaimValueSelect
            key={update.field}
            label={translate('pages:rules.editControls.applyValue')}
            options={options}
            value={update.value}
            onChange={(value) =>
              builder.updateApplyUpdate(update.id, { value })
            }
            testID={
              index === 0 ? 'rule-apply-value' : `rule-apply-value-${index}`
            }
          />
        )}
        {!isTimeBased && !options && (
          <TextInput
            label={translate('pages:rules.editControls.applyValue')}
            value={update.value}
            onChange={(event) =>
              builder.updateApplyUpdate(update.id, {
                value: event.currentTarget.value,
              })
            }
            style={{ flex: '1 1 150px' }}
            data-testid={
              index === 0 ? 'rule-apply-value' : `rule-apply-value-${index}`
            }
          />
        )}
        {update.field === ApplyField.TariffCode ? (
          <Text size="sm" c="dimmed">
            {translate('pages:rules.applyData.addIfMissing')}
          </Text>
        ) : (
          <Select
            label={translate('pages:rules.applyData.writePolicy')}
            data={ruleApplyWritePolicySchema.options.map((value) => ({
              value,
              label: translate(`pages:rules.applyData.${value}`),
            }))}
            value={update.writePolicy ?? 'replace'}
            onChange={(value) => {
              const parsed = ruleApplyWritePolicySchema.safeParse(value);
              if (parsed.success)
                builder.updateApplyUpdate(update.id, {
                  writePolicy: parsed.data,
                });
            }}
            allowDeselect={false}
            data-testid={`rule-apply-policy-${index}`}
          />
        )}
        <ActionIcon
          variant="subtle"
          color="gray"
          disabled={builder.applyUpdates.length === 1}
          onClick={() => builder.removeApplyUpdate(update.id)}
          aria-label={`${translate('pages:rules.editControls.removeUpdate')} ${
            index + 1
          }`}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </Paper>
  );
};

export const RuleApplyDataFields = ({
  builder,
}: {
  builder: RuleBuilder;
}): ReactElement => {
  const { translate } = builder;
  const theme = useMantineTheme();
  const selectedAppearance = theme.variantColorResolver({
    color: 'teal',
    variant: 'light',
    theme,
  });
  const behaviors = [
    { value: 'automatic', Icon: IconBolt },
    { value: 'suggest', Icon: IconBulb },
  ];
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Text fw={700} size="lg">
          {translate('pages:rules.applyData.behaviorTitle')}
        </Text>
        <Text size="sm" c="dimmed">
          {translate('pages:rules.applyData.behaviorDescription')}
        </Text>
        <Radio.Group
          value={builder.applyBehavior ?? 'automatic'}
          onChange={(value) => {
            const parsed = ruleApplyBehaviorSchema.safeParse(value);
            if (parsed.success) builder.setApplyBehavior(parsed.data);
          }}
          aria-label={translate('pages:rules.applyData.behaviorTitle')}
        >
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {behaviors.map(({ value, Icon }) => (
              <Radio.Card
                key={value}
                value={value}
                radius="md"
                p="md"
                bg={
                  builder.applyBehavior === value
                    ? selectedAppearance.background
                    : undefined
                }
                style={{
                  borderColor:
                    builder.applyBehavior === value
                      ? 'var(--mantine-color-teal-6)'
                      : undefined,
                }}
                aria-label={translate(`pages:rules.applyData.${value}Title`)}
              >
                <Group wrap="nowrap" align="flex-start">
                  <ThemeIcon color="teal" variant="light" size="lg" radius="md">
                    <Icon size={20} />
                  </ThemeIcon>
                  <Stack gap={2} flex={1}>
                    <Text fw={600} size="sm">
                      {translate(`pages:rules.applyData.${value}Title`)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {translate(`pages:rules.applyData.${value}Description`)}
                    </Text>
                  </Stack>
                  <Radio.Indicator color="teal" />
                </Group>
              </Radio.Card>
            ))}
          </SimpleGrid>
        </Radio.Group>
      </Stack>
      <Stack gap="sm">
        <Text fw={600}>{translate('pages:rules.applyData.fieldsTitle')}</Text>
        {builder.applyUpdates.map((update, index) => (
          <RuleApplyUpdateFields
            key={update.id}
            builder={builder}
            update={update}
            index={index}
          />
        ))}
        <Button
          variant="subtle"
          leftSection={<IconPlus size={14} />}
          onClick={builder.addApplyUpdate}
          w="fit-content"
        >
          {translate('pages:rules.editControls.addField')}
        </Button>
      </Stack>
      {builder.applyBehavior !== 'suggest' && (
        <Select
          label={translate('pages:rules.applyData.notificationTitle')}
          data={ruleApplyNotificationSchema.options.map((value) => ({
            value,
            label: translate(`pages:rules.applyData.${value}`),
          }))}
          value={builder.applyNotification ?? 'silent'}
          onChange={(value) => {
            const parsed = ruleApplyNotificationSchema.safeParse(value);
            if (parsed.success) builder.setApplyNotification(parsed.data);
          }}
          allowDeselect={false}
          data-testid="rule-apply-notification"
        />
      )}
      <Stack gap="sm">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          {translate('pages:rules.applyData.previewTitle')}
        </Text>
        <Paper withBorder p="md" radius="md" style={{ borderStyle: 'dashed' }}>
          <RuleApplyDataPreview {...builder} />
        </Paper>
      </Stack>
      <TextInput
        label={translate('pages:rules.editControls.priority')}
        value={builder.applyPriority}
        onChange={(event) =>
          builder.setApplyPriority(event.currentTarget.value)
        }
        data-testid="rule-apply-priority"
      />
    </Stack>
  );
};
